import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { ArrowForward as ArrowForwardIcon } from '@mui/icons-material';
import dayjs from 'dayjs';
import { formatDate } from '../../utils.ts';
import { SortDirection } from '../../types.ts';
import type {
  StatusFilterValue,
  TApiApplication,
  TColumnDef,
  TPageConfiguration,
} from '../../types.ts';
import { ACTION_TOOLTIP, COVENANTS_DATE_TIME_FORMAT } from '~/modules/covenants/constants.tsx';
import { TablePaginationControls } from '~/components/TablePaginationControls/TablePaginationControls.tsx';
import { TableHeaderWithTooltip } from '~/modules/covenants/CovenantsDataForm/components/TableHeaderWithTooltip.tsx';
import { SortableHeader } from '~/modules/covenants/CovenantsDataForm/components/TableSortComponents/SortableHeader.tsx';
import {
  StatusChip,
  StatusFilterHeader,
} from '~/modules/covenants/CovenantsDataForm/components/TableSortComponents/StatusFilterHeader.tsx';
import { commonStyles } from '~/modules/covenants/CovenantsDataForm/commonStyles.ts';
import { dataFormStyles } from '~/modules/covenants/CovenantsDataForm/styles.ts';
import { covConstantsStyles } from '~/modules/covenants/constantsStyles.ts';
import { mergeSx } from '~/modules/covenants/tableUtils.ts';

interface IApplicationTableViewProps {
  applications: TApiApplication[];
  page: number;
  rowsPerPage: number;
  pageConfiguration: TPageConfiguration;
  isLoading: boolean;
  statusFilter: StatusFilterValue;
  onStatusFilterChange: (value: StatusFilterValue) => void;
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (rowsPerPage: number) => void;
  onViewApplication: (app: TApiApplication) => void;
}

const ApplicationTableView: React.FC<IApplicationTableViewProps> = ({
  applications,
  page,
  rowsPerPage,
  pageConfiguration,
  isLoading,
  statusFilter,
  onStatusFilterChange,
  onPageChange,
  onRowsPerPageChange,
  onViewApplication,
}) => {
  const [sortDirection, setSortDirection] = useState<SortDirection>(SortDirection.None);

  const toggleSort = () => {
    setSortDirection((prev) => {
      if (prev === SortDirection.None) return SortDirection.Asc;
      if (prev === SortDirection.Asc) return SortDirection.Desc;
      return SortDirection.None;
    });
  };

  const filtered = useMemo(() => {
    if (sortDirection === SortDirection.None) {
      return applications;
    }

    return [...applications].sort((a, b) => {
      const aTimestamp = dayjs(a.createdAt).isValid()
        ? dayjs(a.createdAt).valueOf()
        : Number.NEGATIVE_INFINITY;
      const bTimestamp = dayjs(b.createdAt).isValid()
        ? dayjs(b.createdAt).valueOf()
        : Number.NEGATIVE_INFINITY;
      const diff = aTimestamp - bTimestamp;

      return sortDirection === SortDirection.Asc ? diff : -diff;
    });
  }, [applications, sortDirection]);

  const columns: TColumnDef<TApiApplication>[] = [
    {
      key: 'name',
      header: 'Заявки',
      align: 'left',
      render: (app) => (
        <Typography
          variant="body2"
          sx={mergeSx(
            covConstantsStyles.tablePrimaryText,
            covConstantsStyles.tablePrimaryTextSingleLine
          )}
        >
          {app.localId != null ? `Заявка №${app.localId}` : app.displayName}
        </Typography>
      ),
    },
    {
      key: 'createdAt',
      header: 'Дата створення',
      align: 'left',
      headerComponent: (
        <SortableHeader
          label="Дата створення"
          direction={sortDirection}
          onToggle={toggleSort}
        />
      ),
      render: (app) => (
        <Typography variant="body2" sx={covConstantsStyles.tableDateText}>
          {app.createdAt ? formatDate(app.createdAt, COVENANTS_DATE_TIME_FORMAT) : '-'}
        </Typography>
      ),
    },
    {
      key: 'status',
      header: 'Статус',
      align: 'left',
      headerComponent: (
        <StatusFilterHeader
          label="Статус"
          filter={statusFilter}
          onSelect={onStatusFilterChange}
        />
      ),
      render: (app) => <StatusChip status={app.status} />,
    },
    {
      key: 'actions',
      header: 'Дія',
      align: 'left',
      headerComponent: (
        <TableHeaderWithTooltip
          label="Дія"
          tooltip={ACTION_TOOLTIP}
          ariaLabel="Опис колонки Дія"
        />
      ),
      render: (app: TApiApplication) => (
        <Tooltip title={ACTION_TOOLTIP} arrow>
          <Button
            variant="outlined"
            size="small"
            endIcon={<ArrowForwardIcon />}
            onClick={() => onViewApplication(app)}
            sx={covConstantsStyles.covBtn}
          >
            Переглянути
          </Button>
        </Tooltip>
      ),
    },
  ];

  return (
    <Box sx={{ mt: 4 }}>
      <Paper elevation={0} sx={dataFormStyles.documentTableSection}>
        <Box sx={dataFormStyles.documentTableToolbar}>
          <Typography variant="h6" sx={dataFormStyles.documentTableToolbarTitle}>
            Подані заявки
          </Typography>
        </Box>

        <Box sx={commonStyles.loadingWrap}>
          <TableContainer sx={dataFormStyles.documentTableContainer}>
            <Table sx={dataFormStyles.documentTable}>
              <TableHead sx={dataFormStyles.documentTableHead}>
                <TableRow>
                  {columns.map((col) => (
                    <TableCell
                      key={col.key}
                      align={col.align}
                      sx={(theme) => dataFormStyles.documentTableHeadCell(theme, col.key)}
                    >
                      {col.headerComponent ?? col.header}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {!filtered.length ? (
                  <TableRow>
                    <TableCell
                      colSpan={columns.length}
                      align="center"
                      sx={dataFormStyles.documentEmptyCell}
                    >
                      Заяви відсутні
                    </TableCell>
                  </TableRow>
                ) : (
                  filtered.map((app) => (
                    <TableRow key={app.id} hover sx={dataFormStyles.documentTableRow}>
                      {columns.map((col) => (
                        <TableCell
                          key={col.key}
                          align={col.align}
                          sx={(theme) => dataFormStyles.documentTableBodyCell(theme, col.key)}
                        >
                          {col.render(app)}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {isLoading && (
            <Box sx={commonStyles.loadingMask}>
              <CircularProgress size={28} />
            </Box>
          )}
        </Box>

        <TablePaginationControls
          page={page}
          rowsPerPage={rowsPerPage}
          totalElements={pageConfiguration.totalElements}
          isLoading={isLoading}
          onPageChange={onPageChange}
          onRowsPerPageChange={onRowsPerPageChange}
        />
      </Paper>
    </Box>
  );
};

export default ApplicationTableView;
